import React, {Fragment} from 'react';

import find from 'lodash/find';

import {HeadingText} from 'app/components/general/HeadingText';
import {InlineStepNumberedTitle} from 'app/procedures/components/InlineStepNumberAndTitle';
import {gt} from 'app/utils/i18n';
import {HTMLDiffDiv} from 'app/utils/showdiff';

import type {ProcedureStepWithProgressType} from 'app/tasks/types';
import type {AnyStepType} from 'sweet-types';

interface Props<T> {
  next:
    | void
    | null
    | string
    | {
        old: string | undefined;
        new: string | undefined;
      };
  steps: Array<T>;
}

export function NextStepLink<
  T extends AnyStepType | ProcedureStepWithProgressType
>({next, steps}: Props<T>) {
  if (next == null) {
    return null;
  } else if (typeof next === 'string') {
    const link = getStepLink(next, steps);
    return link != null ? getOuterText(link) : null;
  } else if (next.old != null || next.new != null) {
    const oldLink = getStepLink(next.old, steps);
    const newLink = getStepLink(next.new, steps);
    return oldLink != null && newLink != null
      ? getOuterText(
          <span>
            <del>{oldLink}</del> <ins>{newLink}</ins>
          </span>
        )
      : oldLink != null
      ? getOuterText(oldLink, 'del')
      : newLink != null
      ? getOuterText(newLink, 'ins')
      : null;
  }

  return null;
}

function getOuterText(
  link: React.ReactNode,
  wrapper: string | React.ElementType = Fragment
) {
  return (
    <h4 className="m-0">
      <HeadingText size="0.8rem">
        <HTMLDiffDiv className="d-flex justify-content-center align-items-center">
          {React.createElement(wrapper, {}, gt('Next step %s', link))}
        </HTMLDiffDiv>
      </HeadingText>
    </h4>
  );
}

function getStepLink<T extends AnyStepType>(
  id: string | undefined,
  steps: Array<T>
): undefined | JSX.Element {
  const step: AnyStepType | undefined = find<any>(steps, {id: id as string});

  return step != null ? (
    <a
      href={`#${step.id}`}
      onClick={(e) => e.stopPropagation()}
      className="px-1"
    >
      <InlineStepNumberedTitle
        type={step.type}
        n={step.n}
        title={'title' in step ? step.title : undefined}
      />
    </a>
  ) : undefined;
}
